import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useI18n } from '../i18n';
import { ArrowUpTrayIcon } from '../storage/icons';
import { Portal } from './Portal';

/**
 * DÉPÔT PLEIN ÉCRAN — voile de glisser-déposer de la coquille authentifiée (XFILE-84).
 *
 * CE QUE ÇA RÉSOUT. Jusqu'ici, glisser un fichier depuis le bureau n'avait d'effet qu'en VISANT la
 * zone en pointillés de `/upload` ou le panneau « Déposer ici » de l'explorateur. Rater la cible ne
 * faisait pas « rien » : le navigateur OUVRAIT le fichier dans l'onglet, ce qui détruit la SPA —
 * file d'attente d'envoi en cours comprise. Le voile rend la fenêtre ENTIÈRE réceptive.
 *
 * ⚠ **L'ÉCRAN S'INSCRIT ; SANS INSCRIPTION, IL NE SE PASSE RIEN — C'EST LE POINT STRUCTURANT.**
 * `/trash` et `/admin` n'ont pas de voile, et ce n'est PAS obtenu par une liste noire de routes
 * dans la coquille (qu'il faudrait tenir à jour, et qui donnerait un voile à toute route future par
 * défaut) : l'effet qui pose les écouteurs SORT quand aucune cible n'est inscrite. Un écran qui ne
 * s'inscrit pas n'a donc ni voile, ni `preventDefault`, ni écouteur — le comportement du navigateur
 * y est rigoureusement celui d'avant nous. Même esprit que le prédicat structurel
 * `meta.isSpaceRoot` préféré à une liste d'UUID (ADR-0018).
 *
 * MÉCANISME — calqué sur `components/Breadcrumb.tsx` : le provider vit dans la coquille, la page
 * POUSSE sa cible ({@link useDropFiles}), la coquille REND le voile, et l'inscription est RETIRÉE
 * au démontage de la page. Sans ce retrait, quitter un dossier pour la console laisserait un voile
 * qui promettrait un dépôt dans un dossier qu'on ne regarde plus.
 *
 * ⚠ **LE `label` EST FOURNI DÉJÀ TRADUIT PAR L'ÉCRAN**, jamais fabriqué ici : seul l'écran sait
 * dire « Déposer dans ce dossier » là où l'accueil dit autre chose, et lui seul a le `t()` et le
 * nom du dossier. Le voile fournit l'icône, la mise en forme et la consigne générique.
 */

/** Cible de dépôt inscrite par l'écran courant. */
export interface DropTarget {
  /** Reçoit les fichiers lâchés n'importe où dans la fenêtre. */
  onFiles: (files: File[]) => void;
  /** Libellé DÉJÀ TRADUIT, affiché dans le voile (« Déposer dans ce dossier »…). */
  label: string;
}

interface DropTargetContextValue {
  /** Inscrit une cible (remplace la précédente). */
  register: (target: DropTarget) => void;
  /** Retire une cible — SEULEMENT si elle est encore l'inscrite (cf. `useDropFiles`). */
  unregister: (target: DropTarget) => void;
  /** Vrai pendant qu'un glisser de FICHIERS survole la fenêtre ET qu'une cible est inscrite. */
  active: boolean;
}

/**
 * Contexte à valeur INERTE par défaut plutôt qu'`undefined`, exactement comme `BreadcrumbContext` :
 * un écran monté HORS coquille — le cas de la plupart des tests unitaires, qui rendent une page
 * seule — doit pouvoir appeler `useDropFiles` sans lever.
 */
const DropTargetContext = createContext<DropTargetContextValue>({
  register: () => {},
  unregister: () => {},
  active: false,
});

/**
 * Un glisser transporte-t-il des FICHIERS ?
 *
 * ⚠ Garde indispensable : `dragenter`/`dragover` se déclenchent aussi pour du texte sélectionné ou
 * un élément interne (une image déplacée dans la page). Lever le voile pour ceux-là promettrait un
 * dépôt qui ne viendra pas. `types` est lisible pendant tout le glisser (contrairement aux DONNÉES,
 * protégées jusqu'au `drop`).
 */
function dragHasFiles(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (types === undefined || types === null) return false;
  return Array.from(types).includes('Files');
}

export function DropTargetProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [target, setTarget] = useState<DropTarget | null>(null);
  const [dragging, setDragging] = useState(false);

  const register = useCallback((next: DropTarget) => setTarget(next), []);
  /**
   * Le retrait est conditionné à l'IDENTITÉ de la cible : si un autre écran s'est inscrit entre
   * temps, le démontage du précédent ne doit pas emporter l'inscription du nouveau. (React vidange
   * bien les cleanups de l'arbre démonté avant les effets du nouveau, mais s'appuyer sur cet ordre
   * pour une donnée partagée serait un pari inutile.)
   */
  const unregister = useCallback(
    (previous: DropTarget) => setTarget((current) => (current === previous ? null : current)),
    [],
  );

  // La cible est lue PAR RÉFÉRENCE au moment du dépôt : une callback recréée à chaque rendu de
  // l'écran ne doit pas débrancher/rebrancher les écouteurs (même idiome que `usePasteFiles`).
  const latest = useRef<DropTarget | null>(target);
  useEffect(() => {
    latest.current = target;
  }, [target]);

  /**
   * `armed` — et non `target` — porte la dépendance de l'effet : seule compte la présence d'une
   * cible, pas son identité. Un simple changement de libellé ne doit pas rejouer le cleanup en
   * plein glisser (le voile clignoterait).
   */
  const armed = target !== null;

  useEffect(() => {
    // ⚠ SORTIE STRUCTURELLE : aucune cible ⇒ AUCUN écouteur, donc aucun `preventDefault`, donc le
    // navigateur se comporte comme avant nous. C'est ce qui donne `/trash` et `/admin` gratuitement.
    if (!armed) return;

    /**
     * ⚠ COMPTEUR, PAS BOOLÉEN. `dragenter`/`dragleave` se déclenchent à CHAQUE changement
     * d'élément survolé : en traversant la page on reçoit `dragenter` sur le nouvel élément PUIS
     * `dragleave` sur l'ancien. Un booléen éteindrait le voile à chaque frontière franchie — il
     * clignoterait tout le long du trajet. Le compteur reste ≥ 1 tant qu'on n'a pas quitté la
     * fenêtre.
     */
    let depth = 0;

    const onDragEnter = (event: DragEvent): void => {
      if (!dragHasFiles(event)) return;
      depth += 1;
      setDragging(true);
    };

    const onDragOver = (event: DragEvent): void => {
      if (!dragHasFiles(event)) return;
      // ⚠ SANS CE `preventDefault`, IL N'Y A PAS DE DÉPÔT POSSIBLE : le navigateur refuse la cible
      // et, au lâcher, OUVRE le fichier dans l'onglet — la SPA est détruite, session et file
      // d'attente comprises. C'est la ligne qui justifie tout le module.
      event.preventDefault();
      if (event.dataTransfer !== null) event.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = (): void => {
      // Jamais sous zéro : un `dragleave` orphelin (glisser commencé hors fenêtre, glisser non
      // porteur de fichiers) ne doit pas mettre le compteur en dette et bloquer le voile suivant.
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };

    /** Fin de glisser sans dépôt (lâché hors fenêtre) : le voile ne doit pas rester. */
    const onDragEnd = (): void => {
      depth = 0;
      setDragging(false);
    };

    const onDrop = (event: DragEvent): void => {
      // Le voile tombe d'abord, quoi qu'il arrive ensuite — y compris si le dépôt appartient à
      // quelqu'un d'autre (ci-dessous) : un voile qui survit au lâcher est un écran bloqué.
      depth = 0;
      setDragging(false);

      /**
       * ⚠ **NE PAS AVALER LE DÉPÔT D'UNE ZONE LOCALE.** Les zones existantes (`Dropzone` de
       * `/upload`, « Déposer ici » de l'explorateur, dépôt communautaire) ont leur propre `onDrop`
       * React, qui appelle `preventDefault()`. React délègue depuis le conteneur racine, DESCENDANT
       * de `window` : leur gestionnaire s'exécute donc AVANT celui-ci. Sans cette garde, un fichier
       * lâché sur la zone en pointillés serait ajouté DEUX fois. Même idiome que `usePasteFiles`.
       */
      if (event.defaultPrevented) return;

      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      latest.current?.onFiles(files);
    };

    // Écoute au niveau de la FENÊTRE : un fichier peut être lâché n'importe où, et l'utilisateur ne
    // « met pas le focus » sur une zone avant de lâcher.
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragend', onDragEnd);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragend', onDragEnd);
      window.removeEventListener('drop', onDrop);
      // Désarmer PENDANT un glisser (changement d'écran) laisserait sinon le drapeau ARMÉ : le
      // voile se rallumerait tout seul au retour sur un écran inscrit, alors que le glisser est
      // fini depuis longtemps et qu'aucun `dragleave` ne viendra plus l'éteindre.
      setDragging(false);
    };
  }, [armed]);

  const active = dragging && target !== null;
  const value = useMemo(() => ({ register, unregister, active }), [register, unregister, active]);

  return (
    <DropTargetContext.Provider value={value}>
      {children}
      {active && target !== null && <DropOverlay label={target.label} />}
    </DropTargetContext.Provider>
  );
}

/**
 * Inscrit la cible de dépôt de l'écran courant, et la RETIRE au démontage.
 *
 * `null` (ou hook non appelé) ⇒ aucun voile, aucun écouteur : c'est le défaut, et il est voulu.
 * L'appelant reconstruit son objet à chaque rendu — seul le `label` porte donc la dépendance de
 * l'effet, la callback étant lue par référence (une `onFiles` recréée à chaque rendu ne doit pas
 * réinscrire la cible).
 */
export function useDropFiles(target: DropTarget | null): void {
  const { register, unregister } = useContext(DropTargetContext);

  const latest = useRef<DropTarget | null>(target);
  useEffect(() => {
    latest.current = target;
  });

  const label = target === null ? null : target.label;

  useEffect(() => {
    if (label === null) return;
    // Cible STABLE tant que le libellé ne change pas : elle délègue à la dernière callback connue.
    const registered: DropTarget = {
      label,
      onFiles: (files) => latest.current?.onFiles(files),
    };
    register(registered);
    return () => unregister(registered);
  }, [label, register, unregister]);
}

/** Vrai pendant qu'un glisser de fichiers survole la fenêtre ET qu'une cible est inscrite. */
export function useIsDropActive(): boolean {
  return useContext(DropTargetContext).active;
}

/**
 * Voile plein écran : dit OÙ le fichier atterrira (libellé de l'écran) et que le lâcher peut se
 * faire n'importe où (consigne générique du catalogue).
 *
 * ⚠ **`pointer-events-none` EST FONCTIONNEL, PAS COSMÉTIQUE.** Un voile qui reçoit le pointeur
 * devient lui-même l'élément survolé : son apparition émet un `dragleave` sur l'élément qu'il
 * recouvre, donc le voile se retire, donc l'élément est de nouveau survolé… soit un clignotement
 * en boucle. On écoute de toute façon au niveau de la fenêtre : le voile n'a aucun événement à
 * recevoir.
 *
 * Rendu dans un PORTAIL (comme les toasts et les modales) : l'en-tête porte `backdrop-blur`, ce qui
 * établit un bloc conteneur pour ses descendants `position: fixed`.
 *
 * ⚠ **AUCUN FOND ASSOMBRISSANT, ET C'EST DÉLIBÉRÉ.** Le réflexe (un `bg-black/50` comme sous une
 * modale) ferait exactement le contraire de ce qu'on promet : l'en-tête vit à `z-10` DANS la
 * coquille tandis que ce voile est portalisé à `z-40`, donc un fond sombre recouvrirait le bouton
 * « Déposer » — l'ancre visuelle que le glisser vient d'allumer. Le signal « toute la fenêtre est
 * réceptive » est donc porté par une BORDURE en pointillés au bord du viewport, qui ne masque rien.
 *
 * A11Y — l'information n'est jamais portée par la seule couleur : icône, pointillés et DEUX lignes
 * de texte. Le panneau est OPAQUE (`bg-surface`), ce qui garantit le contraste du texte quel que
 * soit le contenu recouvert — un fond translucide, lui, en aurait dépendu. `z-40` place le voile
 * SOUS les modales et les toasts (`z-50`), qui restent lisibles.
 */
function DropOverlay({ label }: { label: string }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <Portal>
      <div
        role="status"
        className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center border-4 border-dashed border-brand p-6"
      >
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border-2 border-dashed border-brand-ink bg-surface px-8 py-10 text-center shadow-lg">
          <ArrowUpTrayIcon className="size-8 text-brand-ink" />
          <p className="text-lg font-medium text-fg">{label}</p>
          <p className="text-sm text-muted">{t('drop.hint')}</p>
        </div>
      </div>
    </Portal>
  );
}
